import { Button, PlainButton, Popover, Tooltip } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import { HELP_CENTER_URL } from "@jugl-web/utils/consts";
import React, { useMemo } from "react";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useCXSignupDialog } from "@web-src/modules/cx/components/CXSignupDialog/useCXSignupDialog";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { useUserAuth } from "@web-src/modules/auth/providers/UserAuthProvider/useUserAuth";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { getNextTierPlan } from "@web-src/modules/cx/pages/CXPlansPage/utils/planTierUtils";
import { ReactComponent as MoreMenuIcon } from "./assets/more-menu.svg";
import { ReactComponent as HelpCenterIcon } from "./assets/help-center.svg";
import { ReactComponent as PricingIcon } from "./assets/pricing.svg";
import { ReactComponent as LogoutIcon } from "./assets/logout.svg";

export const CXProfilePopover: React.FC = () => {
  const { t } = useTranslations();
  const { isAnonymous } = useAnonymousSession();
  const cxSignupDialog = useCXSignupDialog();
  const { isAdmin } = useUserRole();
  const { logout } = useUserAuth();
  const { plans, currentPlan, isMonthly, openSubscriptionDrawer } =
    useSubscriptionV2();

  const showBoostPlanButton = useMemo(() => {
    if (!isAdmin || !plans?.length) return false;
    if (!currentPlan || isMonthly) return true;
    return !!getNextTierPlan(currentPlan, plans);
  }, [isAdmin, currentPlan, isMonthly, plans]);

  const menuItems: {
    key: string;
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
  }[] = useMemo(
    () => [
      {
        key: "help-center",
        icon: <HelpCenterIcon />,
        label: t({
          id: "main-navbar-component.help-center",
          defaultMessage: "Help Center",
        }),
        onClick: () => {
          window.open(HELP_CENTER_URL, "_blank");
        },
      },
      {
        key: "logout",
        icon: <LogoutIcon />,
        label: t({
          id: "main-navbar-component.logout",
          defaultMessage: "Logout",
        }),
        onClick: () => {
          logout(true);
        },
      },
    ],
    [t, logout]
  );

  return (
    <Popover
      placement="bottom"
      hasBackdrop
      backdropStyle="transparent"
      // z-index is one higher than Intercom widget
      className="z-[2147483001] w-[280px] overflow-hidden rounded-[20px] bg-dark py-6"
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Tooltip
          isDisabled={isOpen}
          renderTrigger={({ props, ref }) => (
            <div ref={ref} {...props}>
              <Trigger
                ref={triggerRef}
                as={PlainButton}
                onClick={(e) => {
                  if (isAnonymous) {
                    e.preventDefault();
                    cxSignupDialog?.open();
                  }
                }}
                className={cx(
                  "flex h-8 w-8 items-center justify-center rounded-lg transition-colors outline-none hover:bg-[#333333]",
                  isOpen && "bg-[#333333]"
                )}
              >
                <MoreMenuIcon />
              </Trigger>
            </div>
          )}
        >
          {t({
            id: "main-nav-bar-component.more-actions",
            defaultMessage: "More actions",
          })}
        </Tooltip>
      )}
    >
      {({ onClose }) => {
        return (
          <div className="flex flex-col">
            <div className="flex flex-col gap-4 px-6">
              {menuItems.map(({ key, icon, label, onClick }) => (
                <MenuItem
                  key={key}
                  icon={icon}
                  label={label}
                  onClick={() => {
                    onClick();
                    onClose();
                  }}
                />
              ))}
            </div>
            {showBoostPlanButton && (
              <>
                <div className="my-5 h-px w-full shrink-0 bg-dark-900" />
                <div className="px-6">
                  <Button
                    variant="primary"
                    size="md"
                    fullWidth
                    iconEnd={<PricingIcon />}
                    onClick={() => {
                      openSubscriptionDrawer();
                      onClose();
                    }}
                  >
                    {t({
                      id: "main-navbar-component.boost-plan",
                      defaultMessage: "Boost Plan",
                    })}
                  </Button>
                </div>
              </>
            )}
          </div>
        );
      }}
    </Popover>
  );
};

const MenuItem = ({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) => (
  <PlainButton
    className="flex h-12 w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-[#252B2E]"
    onClick={onClick}
  >
    <span className="shrink-0">{icon}</span>
    <span className="font-secondary text-sm leading-[1.5] text-white">
      {label}
    </span>
  </PlainButton>
);
